import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, of, shareReplay, tap } from 'rxjs';
import { HttpCacheService } from './http-cache.service';
import { CACHE_OPTIONS } from './http-cache.tokens';

@Injectable({ providedIn: 'root' })
export class HttpCacheInterceptor implements HttpInterceptor {

    constructor(private readonly httpCacheService: HttpCacheService) {
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const opts = req.context.get(CACHE_OPTIONS);

        // Mutating requests invalidieren ggf. zugehörige GET-Caches
        if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(req.method)) {
            // Optional: gezielt löschen – hier Beispiel: gleicher Pfad-Prefix
            this.httpCacheService.clearByPrefix(`GET|${new URL(req.url, window.location.origin).pathname}`);
            return next.handle(req);
        }

        // Nur GET wird gecacht
        if (req.method !== 'GET' || !opts.enabled) {
            return next.handle(req);
        }

        const key = this.httpCacheService.buildCacheKey(req);
        console.log('Cache key', key);

        const cached = this.httpCacheService.get(key);
        const now = Date.now();

        // TTL gültig?
        const isFresh = cached && (now - cached.addedAt < opts.ttlMs);

        if (isFresh) {
            console.log('Cache hit', key);
            // Sofort ausliefern
            return of(cached.response.clone());
        }

        // Optional: Stale-While-Revalidate – zuerst stale liefern, dann Request im Hintergrund
        if (cached && opts.staleWhileRevalidate) {
            console.log('Cache staleWhileRevalidate', key);

            // Fire-and-forget Hintergrund-Refresh (keine Doppel-Emission hier)
            // Hinweis: Wenn du eine sichtbare Re-Emission brauchst, nutze ein Subject/Store.
            this.refreshInBackground(req, next, key, cached.etag);
            return of(cached.response.clone());
        }

        console.log('Cache miss', key);

        // ETag-Unterstützung: falls vorhanden, setze If-None-Match
        const requestWithEtags = cached?.etag
            ? req.clone({ setHeaders: { 'If-None-Match': cached.etag } })
            : req;

        // Netz-Request ausführen und Ergebnis cachen
        return next.handle(requestWithEtags).pipe(
            tap(event => {
                if (event instanceof HttpResponse) {
                    const etag = event.headers.get('ETag') ?? undefined;
                    this.httpCacheService.set(key, { response: event.clone(), addedAt: Date.now(), etag });
                }
            }),
            // shareReplay sorgt dafür, dass parallele identische Requests nicht mehrfach schießen
            shareReplay(1)
        );

    }


    private refreshInBackground(
        req: HttpRequest<any>,
        next: HttpHandler,
        key: string,
        etag?: string
    ): void {
        const r = etag ? req.clone({ setHeaders: { 'If-None-Match': etag } }) : req;
        next.handle(r).subscribe({
            next: event => {
                if (event instanceof HttpResponse) {
                    const newEtag = event.headers.get('ETag') ?? etag;
                    this.httpCacheService.set(key, { response: event.clone(), addedAt: Date.now(), etag: newEtag });
                }
            },
            // Fehler im Hintergrund nur loggen – kein Einfluss auf UI-Antwort
            error: () => { /* optional logging */
            }
        });
    }

}

